import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useState } from 'react';
import {
  Waves, Lightning, Globe, Gauge, ChartLineUp, ShieldCheck, Users,
  Envelope, Rocket, ArrowLeft, Sun, Moon, List, X,
  NumberCircleOne, NumberCircleTwo, NumberCircleThree, NumberCircleFour
} from '@phosphor-icons/react';
import { Logo, LogoText } from '../components/Logo';

const differentiators = [
  { icon: Lightning, title: 'Faster indexing results', desc: 'Most URLs are processed within promised hours, not days' },
  { icon: Gauge, title: 'Clean, intuitive dashboard', desc: 'Option to submit individual URLs as well as bulk-upload hundreds at once' },
  { icon: ShieldCheck, title: 'Transparent pricing', desc: 'Said by customers, and they pay only for what they use, with no hidden fees or lock-in contracts' },
  { icon: Globe, title: 'Multi-search-engine support', desc: 'We notify major search engines like Bing, Yahoo, not just Google' },
  { icon: ChartLineUp, title: 'Real-time indexing tracking', desc: 'Easily monitor the indexing status for each URL you submit.' },
  { icon: Users, title: '24/7 Dedicated support', desc: 'A trustworthy team is there to assist when difficulties arise.' },
];

const audiences = [
  { label: 'SEO Agencies', desc: 'Who want to manage multiple client websites' },
  { label: 'Affiliate marketers', desc: 'In need of publishing time-sensitive content' },
  { label: 'Website Owners', desc: 'Need their pages indexed without the long waiting time' },
  { label: 'Digital marketers', desc: 'Always running content-led campaigns' },
  { label: 'Developers', desc: 'Launching new pages, features, or migrations' },
];

const steps = [
  { num: NumberCircleOne, title: 'Create your account', desc: 'Sign up and access your dashboard instantly.' },
  { num: NumberCircleTwo, title: 'Submit your URLs', desc: 'Paste a single URL or upload a list of URLs.' },
  { num: NumberCircleThree, title: 'We do the work', desc: 'Our system pings search engines using proven indexing protocols to trigger a crawl.' },
  { num: NumberCircleFour, title: 'Track your results', desc: 'Monitor which URLs have been indexed directly from your dashboard.' },
];

export default function About() {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="about-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/about" className="text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid="about-nav-about">About</Link>
            <Link to="/faq" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="about-nav-faq">FAQ</Link>
            <Link to="/pricing" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="about-nav-pricing">Pricing</Link>
            <Link to="/docs" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="about-nav-docs">API Docs</Link>
            <Link to="/support" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="about-nav-support">Support</Link>
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }} data-testid="about-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/register" className="btn-primary text-sm" data-testid="about-nav-get-started">Get Started</Link>
          </div>
          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" data-testid="about-mobile-menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t px-4 py-4 space-y-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Pricing</Link>
            <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            <Link to="/support" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Support</Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Login</Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn-primary text-sm text-center block">Get Started</Link>
          </div>
        )}
      </nav>

      <section className="pt-24 sm:pt-32 pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="about-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4" data-testid="about-heading">
            We Help Your Pages <span style={{ color: 'var(--accent)' }}>Get Found Fast</span>
          </h1>
          <p className="text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            index24hrs.com is a purpose-built website indexing platform designed for SEO professionals, agencies, and website owners. Those who can't afford to wait days or weeks for their pages to appear in search results. We take the guesswork out of search engine indexing, so your content starts working for you the moment it goes live.
          </p>
        </div>
      </section>

      <section className="pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="border rounded-2xl p-6 sm:p-10" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)', boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Rocket weight="duotone" className="w-7 h-7" style={{ color: 'var(--accent)' }} />
              <h2 className="text-xl sm:text-2xl font-bold">Our Mission</h2>
            </div>
            <p className="leading-relaxed text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              We built index24hrs.com because slow indexing costs real traffic. Fresh backlinks, updated content, and new pages don't affect your results until Google and other search engines have discovered and processed them. We work with a simple mission to eliminate all the unnecessary indexing so that any URL you publish has the best chance of ranking within seconds and consistently.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-3">What Makes Us <span style={{ color: 'var(--accent)' }}>Different</span></h2>
          <p className="text-sm text-center mb-8" style={{ color: 'var(--text-secondary)' }}>The indexing space is crowded. Here's why teams choose index24hrs.com over the alternatives:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.map((item, i) => (
              <div key={i} className="border rounded-xl p-5 transition-all hover:-translate-y-0.5" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid={`about-diff-${i}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--accent-bg)' }}>
                  <item.icon weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                </div>
                <h3 className="font-bold text-sm mb-1">{item.title}</h3>
                <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-3">Who We <span style={{ color: 'var(--accent)' }}>Help</span></h2>
          <p className="text-sm text-center mb-8" style={{ color: 'var(--text-secondary)' }}>Our SEO indexing tool is built for anyone who depends on organic search visibility:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {audiences.map((item, i) => (
              <div key={i} className="border rounded-lg px-4 py-3 flex items-start gap-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }} data-testid={`about-audience-${i}`}>
                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--accent)' }} />
                <div>
                  <p className="font-bold text-sm">{item.label}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-3">How It <span style={{ color: 'var(--accent)' }}>Works</span></h2>
          <p className="text-sm text-center mb-8" style={{ color: 'var(--text-secondary)' }}>Follow these simple 4 steps for getting started with our URL indexing solution:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {steps.map((step, i) => (
              <div key={i} className="border rounded-xl p-5 flex items-start gap-4" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid={`about-step-${i}`}>
                <step.num weight="duotone" className="w-8 h-8 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                <div>
                  <h3 className="font-bold text-sm mb-1">{step.title}</h3>
                  <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="border rounded-2xl p-6 sm:p-10" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Why Teams Trust <span style={{ color: 'var(--accent)' }}>index24hrs.com</span></h2>
            <p className="leading-relaxed text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              Our clients range from individual bloggers to SEO businesses with dozens of client sites. What they have in common is a need for fast, reliable website indexing without the complexity. index24hrs.com provide them with a simple, effective product, clear information, and transparent pricing they can pass on to clients.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="border rounded-2xl p-8 sm:p-12" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)', boxShadow: 'var(--shadow-card)' }}>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Ready to Get Your Pages <span style={{ color: 'var(--accent)' }}>Indexed</span>?</h2>
            <p className="text-sm sm:text-base mb-6" style={{ color: 'var(--text-secondary)' }}>
              Stop waiting for Googlebot to find your content on its own. Submit your URLs today and see the difference a dedicated indexing solution makes. Create your account and start indexing in minutes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base" data-testid="about-cta-register">
                <Lightning weight="bold" className="w-5 h-5" /> Create Account
              </Link>
              <Link to="/support" className="text-sm font-medium hover:underline" style={{ color: 'var(--accent)' }}>
                <Envelope weight="fill" className="w-4 h-4 inline mr-1" /> Contact Support
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-bold text-sm">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </div>
          <p className="text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>© 2026 index24hrs.com. Instant indexing for the modern web.</p>
        </div>
      </footer>
    </div>
  );
}
